import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  query,
  orderBy,
} from "firebase/firestore";
import { toast } from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faTrash,
  faRefresh,
} from "@fortawesome/free-solid-svg-icons";
import { Modal, Box, Typography, Button } from "@mui/material";

import "../screenstyles/Product.css";

interface RedeemRequest {
  id: string;

  offerId: string;
  offerName: string;
  offerType: string;
  requestDate: any;
  requestStatus: string;
  requiredPoints: number;
  userId: string;

  userFullName?: string;
  userAccountLevel?: string;
  userAccountType?: string;
  userPoints?: string | number;
}

const RedeemRequests: React.FC = () => {
  const [requests, setRequests] = useState<RedeemRequest[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState<boolean>(false);

  const formatDate = (value: any) => {
    if (!value) return "-";

    try {
      const date = value.toDate ? value.toDate() : new Date(value);

      return date.toLocaleString("ar-IQ", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "-";
    }
  };

  const fetchRedeemRequests = async () => {
    setLoading(true);

    try {
      /**
       * 1. جلب جميع المستخدمين مرة واحدة
       * حتى لا نعمل طلب منفصل لكل طلب redeemRequest
       */
      const usersSnapshot = await getDocs(collection(db, "users"));

      const usersMap: Record<
        string,
        {
          fullName?: string;
          AccountLevel?: string;
          accountType?: string;
          points?: string | number;
          uid?: string;
        }
      > = {};

      usersSnapshot.forEach((userDoc) => {
        const data = userDoc.data();

        /**
         * نخزن المستخدم بطريقتين:
         * 1. حسب id الدوكيومنت
         * 2. حسب حقل uid إذا موجود
         *
         * لأن أحياناً userId داخل الطلب يكون هو uid
         * وأحياناً يكون هو document id
         */
        usersMap[userDoc.id] = data;

        if (data.uid) {
          usersMap[data.uid] = data;
        }
      });

      /**
       * 2. جلب طلبات الاستبدال
       */
      const requestsQuery = query(
        collection(db, "redeemRequests"),
        orderBy("requestDate", "desc")
      );

      const requestsSnapshot = await getDocs(requestsQuery);

      const fetchedRequests: RedeemRequest[] = [];

      requestsSnapshot.forEach((requestDoc) => {
        const data = requestDoc.data();

        const userData = usersMap[data.userId];

        fetchedRequests.push({
          id: requestDoc.id,

          offerId: data.offerId ?? "",
          offerName: data.offerName ?? "",
          offerType: data.offerType ?? "",
          requestDate: data.requestDate ?? null,
          requestStatus: data.requestStatus ?? "",
          requiredPoints: data.requiredPoints ?? 0,
          userId: data.userId ?? "",

          userFullName: userData?.fullName ?? "غير معروف",
          userAccountLevel: userData?.AccountLevel ?? "-",
          userAccountType: userData?.accountType ?? "-",
          userPoints: userData?.points ?? "-",
        });
      });

      setRequests(fetchedRequests);
    } catch (error) {
      console.error("Error fetching redeem requests:", error);

      toast.error("حدث خطأ أثناء جلب طلبات الاستبدال", {
        duration: 3000,
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRedeemRequests();
  }, []);

  const q = searchQuery.trim().toLowerCase();

  const filteredRequests = requests.filter((request) => {
    if (!q) return true;

    const offerName = String(request.offerName ?? "").toLowerCase();
    const offerType = String(request.offerType ?? "").toLowerCase();
    const status = String(request.requestStatus ?? "").toLowerCase();
    const fullName = String(request.userFullName ?? "").toLowerCase();
    const accountType = String(request.userAccountType ?? "").toLowerCase();
    const accountLevel = String(request.userAccountLevel ?? "").toLowerCase();
    const points = String(request.userPoints ?? "").toLowerCase();

    return (
      offerName.includes(q) ||
      offerType.includes(q) ||
      status.includes(q) ||
      fullName.includes(q) ||
      accountType.includes(q) ||
      accountLevel.includes(q) ||
      points.includes(q)
    );
  });

  const handleOpenModal = (id: string) => {
    setSelectedRequestId(id);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setSelectedRequestId(null);
    setOpenModal(false);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteDoc(doc(db, "redeemRequests", id));

      toast.success("تم حذف طلب الاستبدال بنجاح", {
        duration: 3000,
        position: "top-right",
        style: {
          backgroundImage:
            "linear-gradient(to top, rgb(25, 113, 132), rgb(38, 155, 180))",
          color: "#fff",
          fontSize: "16px",
        },
      });

      handleCloseModal();
      fetchRedeemRequests();
    } catch (error) {
      console.error("Error deleting redeem request:", error);

      toast.error("حدث خطأ أثناء حذف الطلب", {
        duration: 3000,
        position: "top-right",
      });
    }
  };

  const getStatusStyle = (status: string) => {
    switch (status) {
      case "pending":
        return {
          backgroundColor: "#fff7ed",
          color: "#c2410c",
          padding: "4px 10px",
          borderRadius: "999px",
          fontWeight: "bold",
        };

      case "approved":
        return {
          backgroundColor: "#dcfce7",
          color: "#166534",
          padding: "4px 10px",
          borderRadius: "999px",
          fontWeight: "bold",
        };

      case "rejected":
        return {
          backgroundColor: "#fee2e2",
          color: "#991b1b",
          padding: "4px 10px",
          borderRadius: "999px",
          fontWeight: "bold",
        };

      default:
        return {
          backgroundColor: "#f3f4f6",
          color: "#374151",
          padding: "4px 10px",
          borderRadius: "999px",
          fontWeight: "bold",
        };
    }
  };

  return (
    <div className="product-page">
      <div
        className="max-w-full flex items-center justify-between bg-[#262d34]
        text-white px-6 py-3 rounded-t-lg shadow-md"
      >
        <h1
          className="text-xl font-bold flex-1"
          style={{ fontFamily: "Tajawal, sans-serif" }}
        >
          طلبات استبدال النقاط
        </h1>

        <div className="flex items-center bg-gray-100 rounded-full px-4 py-1 w-72 shadow-md ml-2">
          <FontAwesomeIcon icon={faSearch} className="text-gray-500 mr-2" />

          <input
            type="text"
            placeholder="البحث في الطلبات..."
            className="bg-transparent focus:outline-none w-full text-sm text-gray-700 py-1 font-custom"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <FontAwesomeIcon
          icon={faRefresh}
          size="lg"
          className="cursor-pointer hover:opacity-80 transition ml-4"
          onClick={fetchRedeemRequests}
          title="تحديث البيانات"
        />
      </div>

      {loading ? (
        <p style={{ padding: 20, fontFamily: "Tajawal, sans-serif" }}>
          جاري تحميل طلبات الاستبدال...
        </p>
      ) : (
        <div className="table-container">
          <table className="product-table">
            <thead className="color-table">
              <tr>
                <th>#</th>

                <th className="price-column">اسم المستخدم</th>
                <th className="price-column">نوع الحساب</th>
                <th className="price-column">المستوى</th>
                <th className="price-column">نقاط المستخدم</th>

                <th className="price-column">اسم العرض</th>
                <th className="price-column">نوع العرض</th>
                <th className="price-column">النقاط المطلوبة</th>
                <th className="price-column">حالة الطلب</th>
                <th className="price-column">تاريخ الطلب</th>

                <th className="price-column">معرف العرض</th>
                <th className="price-column">معرف المستخدم</th>

                <th className="price-column">الإجراءات</th>
              </tr>
            </thead>

            <tbody>
              {filteredRequests.length === 0 ? (
                <tr>
                  <td colSpan={13} style={{ padding: 20 }}>
                    لا توجد طلبات استبدال
                  </td>
                </tr>
              ) : (
                filteredRequests.map((request, index) => (
                  <tr key={request.id}>
                    <td>{index + 1}</td>

                    <td style={{ fontSize: 16 }}>{request.userFullName}</td>
                    <td style={{ fontSize: 16 }}>{request.userAccountType}</td>
                    <td style={{ fontSize: 16 }}>{request.userAccountLevel}</td>
                    <td style={{ fontSize: 16 }}>{request.userPoints}</td>

                    <td style={{ fontSize: 16 }}>{request.offerName}</td>
                    <td style={{ fontSize: 16 }}>{request.offerType}</td>
                    <td style={{ fontSize: 16 }}>{request.requiredPoints}</td>

                    <td style={{ fontSize: 16 }}>
                      <span style={getStatusStyle(request.requestStatus)}>
                        {request.requestStatus}
                      </span>
                    </td>

                    <td style={{ fontSize: 16 }}>
                      {formatDate(request.requestDate)}
                    </td>

                    <td style={{ fontSize: 13 }}>
                      <div
                        style={{
                          maxWidth: 160,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={request.offerId}
                      >
                        {request.offerId}
                      </div>
                    </td>

                    <td style={{ fontSize: 13 }}>
                      <div
                        style={{
                          maxWidth: 180,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={request.userId}
                      >
                        {request.userId}
                      </div>
                    </td>

                    <td>
                      <div className="icon-container">
                        <button
                          className="icon-button"
                          onClick={() => handleOpenModal(request.id)}
                        >
                          <FontAwesomeIcon
                            icon={faTrash}
                            title="Delete"
                            style={{ color: "red" }}
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={openModal} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 420,
            bgcolor: "white",
            borderRadius: "8px",
            boxShadow: 24,
            p: 3,
          }}
        >
          <Typography
            variant="h6"
            component="h2"
            sx={{
              textAlign: "center",
              marginBottom: 2,
              fontFamily: "Tajawal, sans-serif",
            }}
          >
            هل أنت متأكد من حذف طلب الاستبدال؟
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
            <Button
              variant="contained"
              color="error"
              onClick={() =>
                selectedRequestId && handleDelete(selectedRequestId)
              }
            >
              نعم، احذف الطلب
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              onClick={handleCloseModal}
            >
              إلغاء
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default RedeemRequests;